import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Section from '../components/common/Section';
import CTAFooter from '../components/home/CTAFooter';
import { Megaphone, Palette, Globe, Smartphone, Bot, Code, ShoppingBag, Sparkles, ArrowRight } from 'lucide-react';
import servicesBg from '../assets/images/services-main.png';

const services = [
    {
        icon: Megaphone,
        title: 'Digital Marketing & SEO Services',
        desc: 'SEO Company Pune, Performance Ads (Google/Meta), Social Media Management & Brand Strategy',
        path: '/services/digital-marketing',
        color: 'text-orange-400',
        gradient: 'from-orange-500/20 to-red-500/5'
    },
    {
        icon: Globe,
        title: 'Web Application Development',
        desc: 'Web Development Company in Pune - Custom Web Apps, Responsive Websites, & SaaS Platforms',
        path: '/services/web-development',
        color: 'text-[#bebd19]',
        gradient: 'from-[#bebd19]/20 to-[#a3a215]/5'
    },
    {
        icon: Smartphone,
        title: 'Mobile Application Development',
        desc: 'Mobile App Development Company Pune - Custom iOS, Android, Flutter & React Native Solutions',
        path: '/services/mobile-development',
        color: 'text-[#d4d339]',
        gradient: 'from-[#d4d339]/20 to-[#b5b42b]/5'
    },
    {
        icon: Code,
        title: 'Software Development & SaaS',
        desc: 'Software Development Company Pune - Custom Business Software, MVP Development & Enterprise Solutions',
        path: '/services/software-development',
        color: 'text-blue-400',
        gradient: 'from-blue-500/20 to-cyan-500/5'
    },
    {
        icon: Bot,
        title: 'AI Automation Solutions',
        desc: 'AI Automation Company Pune - Workflow Automation, Custom AI Chatbots & Process Optimization',
        path: '/services/ai-automation',
        color: 'text-[#e5e48d]',
        gradient: 'from-[#e5e48d]/20 to-[#bebd19]/5'
    },
    {
        icon: Palette,
        title: 'UX/UI Design',
        desc: 'User Research, Wireframing, Interactive Prototyping & Visual Design Systems',
        path: '/services/ui-ux-design',
        color: 'text-pink-400',
        gradient: 'from-pink-500/20 to-rose-500/5'
    },
    {
        icon: Sparkles,
        title: 'AEO / GEO Services',
        desc: 'Generative Engine Optimization & AI Search Optimization for ChatGPT, Perplexity & Google SGE',
        path: '/services/aeo-geo',
        color: 'text-purple-400',
        gradient: 'from-purple-500/20 to-indigo-500/5'
    },
    {
        icon: ShoppingBag,
        title: 'E-commerce Development',
        desc: 'Ecommerce Website Development Pune - Custom Online Stores, Shopify & WooCommerce Solutions',
        path: '/services/ecommerce',
        color: 'text-emerald-400',
        gradient: 'from-emerald-500/20 to-teal-500/5'
    }
];

const Services = () => {
    const schemaData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": "https://pyrusmedia.in/services#services",
        "url": "https://pyrusmedia.in/services",
        "name": "Digital Marketing and IT Services | PyrusMedia",
        "description": "Explore PyrusMedia digital marketing, web development, mobile app development, AI automation, and custom IT solutions.",
        "isPartOf": {
            "@id": "https://pyrusmedia.in/#website"
        },
        "about": {
            "@type": "Organization",
            "name": "PyrusMedia",
            "url": "https://pyrusmedia.in/"
        }
    };

    return (
        <>
            <Helmet>
                <title>Digital Marketing Services & IT Solutions in Pune | PyrusMedia</title>
                <meta name="description" content="Explore our full suite of digital marketing services, custom web development, mobile app development, software solutions, and AI automation in Pune" />
                <meta name="keywords" content="digital marketing services in Pune, digital solutions company, IT solutions company Pune, custom digital solutions, web development company Pune, mobile app development company Pune, software development company Pune, SEO company Pune" />
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            </Helmet>

            <section className="pt-32 pb-20 relative overflow-hidden text-center min-h-[50vh] flex flex-col justify-center">
                <div className="absolute inset-0 bg-white">
                    <img src={servicesBg} alt="Digital Services Tech Nodes" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-black/10 to-black/10" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 tracking-tight">
                        Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bebd19] to-yellow-500">Digital Solutions</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                        PyrusMedia provides full-stack digital marketing services, software engineering, mobile apps, and AI automation solutions tailored for businesses in Pune and globally.
                    </p>
                </div>
            </section>

            <Section className="min-h-screen">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.map((service, index) => (
                        <Link
                            key={index}
                            to={service.path}
                            className="group relative rounded-2xl p-[1px] overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent group-hover:from-[#bebd19]/50 group-hover:to-[#a3a215]/50 transition-colors duration-500" />
                            <div className="relative h-full bg-gray-50/90 backdrop-blur-xl p-8 rounded-2xl hover:bg-gray-50/80 transition-colors flex flex-col items-start">

                                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${service.gradient} blur-[40px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                                <div className="relative z-10 w-full flex flex-col h-full">
                                    <service.icon className={`w-12 h-12 mb-6 ${service.color}`} />
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                                    <p className="text-gray-500 mb-6 flex-grow">{service.desc}</p>
                                    <span className="mt-auto flex items-center text-sm font-semibold text-gray-900 group-hover:text-[#bebd19] transition-colors">
                                        View details <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </Section>

            <CTAFooter />
        </>
    );
};

export default Services;

